/**
 * Admin E-commerce Dashboard - Comprehensive Sales Analytics
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShoppingCart, Package, DollarSign, TrendingUp, Users,
  ChevronLeft, RefreshCw, Crown, Radio, Calendar,
  MapPin, BarChart3, PieChart, Activity, Zap,
  UserPlus, Repeat, CreditCard, Box, Target
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

// Stats card component
const StatCard = ({ icon: Icon, label, value, subValue, iconColor = "text-primary", bgColor = "bg-primary/10", trend }) => (
  <Card className="bg-card">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-xl ${bgColor} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
        {trend !== undefined && (
          <span className={`text-xs px-2 py-1 rounded-full ${trend > 0 ? 'bg-green-500/20 text-green-400' : trend < 0 ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      <div className="mt-3">
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-sm text-muted-foreground">{label}</p>
        {subValue && <p className="text-xs text-muted-foreground mt-1">{subValue}</p>}
      </div>
    </CardContent>
  </Card>
);

// Mini bar chart component
const MiniBarChart = ({ data, height = 100 }) => {
  if (!data?.length) return <p className="text-sm text-muted-foreground text-center py-4">Sin datos</p>;
  
  const maxValue = Math.max(...data.map(d => d.revenue || 0), 1);
  const last7 = data.slice(-7);
  
  return (
    <div className="flex items-end justify-between gap-1" style={{ height }}>
      {last7.map((item, idx) => (
        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
          <div 
            className="w-full bg-primary rounded-t transition-all hover:bg-primary/80"
            style={{ 
              height: `${Math.max(4, (item.revenue / maxValue) * height * 0.8)}px`,
              minHeight: '4px'
            }}
            title={`${item.date}: $${item.revenue?.toLocaleString()}`}
          />
          <span className="text-[10px] text-muted-foreground">
            {new Date(item.date + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'short' }).slice(0, 2)}
          </span>
        </div>
      ))}
    </div>
  );
};

// Donut chart component for breakdown
const DonutChart = ({ data, size = 120 }) => {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <p className="text-sm text-muted-foreground text-center">Sin datos</p>;
  
  const colors = ['#22c55e', '#3b82f6', '#a855f7', '#f59e0b', '#ef4444'];
  let currentAngle = 0;
  
  const segments = Object.entries(data).map(([key, value], idx) => {
    const percentage = (value / total) * 100;
    const angle = (percentage / 100) * 360;
    const startAngle = currentAngle;
    currentAngle += angle;
    
    const x1 = 50 + 40 * Math.cos((startAngle - 90) * Math.PI / 180);
    const y1 = 50 + 40 * Math.sin((startAngle - 90) * Math.PI / 180);
    const x2 = 50 + 40 * Math.cos((startAngle + angle - 90) * Math.PI / 180);
    const y2 = 50 + 40 * Math.sin((startAngle + angle - 90) * Math.PI / 180);
    const largeArc = angle > 180 ? 1 : 0;
    
    return {
      key,
      value,
      percentage,
      color: colors[idx % colors.length],
      path: `M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`
    };
  });
  
  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} viewBox="0 0 100 100">
        {segments.map((seg, idx) => (
          <path
            key={idx}
            d={seg.path}
            fill={seg.color}
            className="hover:opacity-80 transition-opacity"
          />
        ))}
        <circle cx="50" cy="50" r="25" fill="hsl(var(--card))" />
      </svg>
      <div className="space-y-1">
        {segments.map((seg, idx) => (
          <div key={idx} className="flex items-center gap-2 text-xs">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: seg.color }} />
            <span className="capitalize">{seg.key}</span>
            <span className="text-muted-foreground">{seg.percentage.toFixed(0)}%</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Top products table
const TopProductsTable = ({ products }) => {
  if (!products?.length) return <p className="text-sm text-muted-foreground text-center py-4">Sin datos</p>;
  
  return (
    <div className="space-y-2">
      {products.slice(0, 5).map((product, idx) => (
        <div key={idx} className="flex items-center justify-between p-2 rounded-lg bg-secondary/30">
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold text-muted-foreground w-6">#{idx + 1}</span>
            <div>
              <p className="font-medium text-sm">{product.name}</p>
              <p className="text-xs text-muted-foreground">{product.quantity} vendidos</p>
            </div>
          </div>
          <p className="font-bold text-primary">${product.revenue?.toLocaleString()}</p>
        </div>
      ))}
    </div>
  );
};

// Top cities
const TopCitiesTable = ({ cities }) => {
  if (!cities?.length) return <p className="text-sm text-muted-foreground text-center py-4">Sin datos</p>;
  
  const maxRevenue = Math.max(...cities.map(c => c.revenue || 0), 1);
  
  return (
    <div className="space-y-2">
      {cities.slice(0, 5).map((city, idx) => (
        <div key={idx} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <MapPin className="w-3 h-3 text-muted-foreground" />
              {city.city}
            </span>
            <span className="font-medium">${city.revenue?.toLocaleString()}</span>
          </div>
          <div className="h-2 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${(city.revenue / maxRevenue) * 100}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default function AdminEcommerce() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminAPI.getShopStats();
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
      toast.error('Error cargando estadísticas');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">E-commerce Analytics</h1>
              <p className="text-xs text-muted-foreground">Dashboard de ventas y métricas</p>
            </div>
          </div>
          
          <Button variant="outline" size="sm" onClick={fetchStats} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </header>

      {loading && !stats ? (
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="max-w-7xl mx-auto p-4 space-y-6">
          {/* Revenue Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={DollarSign}
              label="Ingresos Totales"
              value={formatCurrency(stats?.total_revenue)}
              subValue={`${stats?.paid_orders || 0} órdenes pagadas`}
              iconColor="text-green-500"
              bgColor="bg-green-500/10"
            />
            <StatCard
              icon={TrendingUp}
              label="Últimos 7 días"
              value={formatCurrency(stats?.revenue_7d)}
              iconColor="text-blue-500"
              bgColor="bg-blue-500/10"
            />
            <StatCard
              icon={Calendar}
              label="Últimos 30 días"
              value={formatCurrency(stats?.revenue_30d)}
              iconColor="text-purple-500"
              bgColor="bg-purple-500/10"
            />
            <StatCard
              icon={ShoppingCart}
              label="Ticket Promedio"
              value={formatCurrency(stats?.avg_order_value)}
              subValue={`${stats?.total_units_sold || 0} unidades vendidas`}
              iconColor="text-orange-500"
              bgColor="bg-orange-500/10"
            />
          </div>

          {/* Charts Row */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Tendencia de Ventas (7 días)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <MiniBarChart data={stats?.revenue_by_day} height={120} />
              </CardContent>
            </Card>

            {/* Revenue by Type */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <PieChart className="w-4 h-4" />
                  Ingresos por Tipo de Producto
                </CardTitle>
              </CardHeader>
              <CardContent>
                <DonutChart 
                  data={{
                    'Físico': stats?.revenue_by_type?.physical || 0,
                    'Digital': stats?.revenue_by_type?.digital || 0,
                    'Otro': stats?.revenue_by_type?.other || 0,
                  }} 
                />
              </CardContent>
            </Card>
          </div>

          {/* User & Customer Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatCard
              icon={Users}
              label="Usuarios Totales"
              value={stats?.users?.total || 0}
              iconColor="text-blue-500"
              bgColor="bg-blue-500/10"
            />
            <StatCard
              icon={Crown}
              label="Premium Activos"
              value={stats?.premium?.active_users || 0}
              subValue={`${formatCurrency(stats?.premium?.revenue)} en upgrades`}
              iconColor="text-yellow-500"
              bgColor="bg-yellow-500/10"
            />
            <StatCard
              icon={UserPlus}
              label="Nuevos (30d)"
              value={stats?.users?.new_30d || 0}
              subValue={`${stats?.users?.new_7d || 0} esta semana`}
              iconColor="text-green-500"
              bgColor="bg-green-500/10"
            />
            <StatCard
              icon={Repeat}
              label="Clientes Recurrentes"
              value={stats?.customers?.repeat || 0}
              subValue={`de ${stats?.customers?.unique || 0} únicos`}
              iconColor="text-purple-500"
              bgColor="bg-purple-500/10"
            />
            <StatCard
              icon={Target}
              label="Tasa Conversión"
              value={`${stats?.customers?.conversion_rate || 0}%`}
              iconColor="text-orange-500"
              bgColor="bg-orange-500/10"
            />
          </div>

          {/* Revenue by Segment */}
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Ingresos por Segmento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-500/10">
                  <div className="flex items-center gap-2">
                    <Radio className="w-4 h-4 text-blue-500" />
                    <span>Club Owners</span>
                  </div>
                  <span className="font-bold text-blue-500">{formatCurrency(stats?.revenue_by_segment?.club_owners)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-500/10">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-green-500" />
                    <span>Jugadores</span>
                  </div>
                  <span className="font-bold text-green-500">{formatCurrency(stats?.revenue_by_segment?.players)}</span>
                </div>
              </CardContent>
            </Card>

            {/* Shelly Buttons */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Botones Shelly
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 rounded-lg bg-secondary/50 text-center">
                    <p className="text-2xl font-bold">{stats?.shelly_buttons?.sold || 0}</p>
                    <p className="text-xs text-muted-foreground">Vendidos</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50 text-center">
                    <p className="text-2xl font-bold">{stats?.shelly_buttons?.active || 0}</p>
                    <p className="text-xs text-muted-foreground">Activos</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50 text-center">
                    <p className="text-2xl font-bold">{stats?.shelly_buttons?.rental || 0}</p>
                    <p className="text-xs text-muted-foreground">Renta</p>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/50 text-center">
                    <p className="text-2xl font-bold">{stats?.shelly_buttons?.resale || 0}</p>
                    <p className="text-xs text-muted-foreground">Reventa</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Products & Cities */}
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Productos Más Vendidos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TopProductsTable products={stats?.top_products} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Top Ciudades
                </CardTitle>
              </CardHeader>
              <CardContent>
                <TopCitiesTable cities={stats?.top_cities} />
              </CardContent>
            </Card>
          </div>

          {/* Orders by Status */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Estado de Órdenes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
                {[
                  { key: 'pendiente', label: 'Pendientes', color: 'bg-yellow-500/20 text-yellow-400' },
                  { key: 'pagado', label: 'Pagadas', color: 'bg-green-500/20 text-green-400' },
                  { key: 'procesando', label: 'Procesando', color: 'bg-blue-500/20 text-blue-400' },
                  { key: 'enviado', label: 'Enviadas', color: 'bg-purple-500/20 text-purple-400' },
                  { key: 'entregado', label: 'Entregadas', color: 'bg-green-500/20 text-green-400' },
                  { key: 'cancelado', label: 'Canceladas', color: 'bg-red-500/20 text-red-400' },
                  { key: 'rechazado', label: 'Rechazadas', color: 'bg-red-500/20 text-red-400' },
                ].map(status => (
                  <div key={status.key} className={`p-3 rounded-lg ${status.color} text-center`}>
                    <p className="text-xl font-bold">{stats?.orders_by_status?.[status.key] || 0}</p>
                    <p className="text-xs">{status.label}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => navigate('/admin')}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              Volver al Dashboard
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/shelly')}>
              <Radio className="w-4 h-4 mr-2" />
              Gestionar Botones
            </Button>
            <Button variant="outline" onClick={() => navigate('/admin/users')}>
              <Users className="w-4 h-4 mr-2" />
              Gestionar Usuarios
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
